import React, { Suspense, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Loader } from 'ui';
import qs from 'qs';
import { equals } from 'utils';
import { makeStyles } from '@mui/styles';
import Paper from '@mui/material/Paper';
import { COLORS } from '@scope/scope-one-ui/theme';

import { requestInstrumentRatingAndFetchDataAction } from 'store/instrument/instrumentActions';
import { ItemType } from 'shared/types/portfolios';
import { usePortfolioModals, useQuery } from 'shared/hooks';
import { moduleTypes } from 'shared/constants/moduleTypes';
import { CreateNewPortfolioModal } from 'modules/common/Modals';
import { CompanyPanel } from 'ui/Company';
import { CompanyCreditRating, Documents } from 'modules/creditRating';
import { formatNumber, getCompanyData, findModule } from 'shared/functions';
import { RoundButton } from 'ui/Buttons';
import { DisclaimerModal } from 'ui/Modals';
import { companyInstrumentModules as companyModulesConst } from 'shared/constants/company';
import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { RatingNews, Research } from './Tabs/Tabs';

const useStyles = makeStyles({
  link: {
    textDecoration: 'none',
    color: COLORS.primary.main,
    transition: '0.2s ease-out',

    '&:hover': {
      color: COLORS.primary.hover,
    },
  },
});

const InstrumentContent = ({
  activeTabs,
  setActiveTabs,
  data,
  instrumentId,
  isRatingRequestPending,
  isPending,
}) => {
  const css = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const { module } = useQuery();
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const { createPortfolio, addToPortfolio } = usePortfolioModals();
  const navigate = useNavigate();

  const toggleDisclaimerModal = () => setShowDisclaimerModal(!showDisclaimerModal);
  const { companyTab, companyModule } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const onChangeTabs = (type, value) => {
    const searchQueryParams = {
      ...(companyTab && { companyTab: activeTabs.companyTab }),
      ...(companyModule && { companyModule: activeTabs.companyModule }),
      [type]: value,
    };

    const params = {
      ...activeTabs,
      [type]: value,
    };

    navigate({
      search: `?${qs.stringify(searchQueryParams)}`,
    });

    setActiveTabs(params);
  };

  const basicData = [
    {
      label: t('Industry'),
      value: data.industry,
    },
    {
      label: t('ISIN'),
      value: data.isin,
    },
    {
      label: t('Seniority'),
      value: data.seniorityType,
    },
    {
      label: t('Currency'),
      value: data.currency,
    },
    {
      label: t('Instrument date'),
      value: data.date,
    },
    {
      label: t('Instrument volume'),
      value: formatNumber(data.volume),
    },
    {
      label: t('Outstanding volume'),
      value: formatNumber(data.outstandingVolume),
    },
    {
      label: t('Outstanding date'),
      value: data.outstandingDate,
    },
    {
      label: t('Coupon percent'),
      value: data.couponPercent,
    },
    {
      label: t('Maturity date'),
      value: data.maturity,
    },
  ].filter(({ value }) => value);

  const {
    ratings,
    level,
    isPublicRating,
    isRatingSubscription,
    showConflictOfInterests,
    showRatingRequestInProgress,
    showRatings,
    showRequestRatingBox,
  } = data && data.products && getCompanyData(data.products);

  const handleRequestRatingClick = () => {
    dispatch(requestInstrumentRatingAndFetchDataAction({ id: instrumentId, level }));
  };

  const isActiveTab = (moduleType) =>
    companyModulesConst.find(findModule(moduleType)) && moduleType === activeTabs.companyModule;

  const itemsForPortfolio = {
    items: { [instrumentId]: instrumentId },
    count: 1,
    type: ItemType.SCOPEONEID,
  };

  return (
    <>
      <CompanyPanel
        loading={isRatingRequestPending || isPending}
        title={
          <>
            {data.name}
            {' - '}
            <Link
              to={`/entities/${data.entityId}${module ? `?module=${module}` : ''}`}
              className={css.link}
            >
              {data.entityName}
            </Link>
          </>
        }
        action={
          <>
            <RoundButton label={t('Add to portfolio')} onClick={addToPortfolio.show} />
            <RoundButton label={t('View disclaimer')} onClick={toggleDisclaimerModal} marginLeft />
          </>
        }
        items={basicData}
        activeModule={activeTabs.companyModule}
        onChangeModule={onChangeTabs}
        modules={companyModulesConst}
        variant="instrument"
      />
      {isActiveTab(moduleTypes.CREDIT_RATING) && (
        <CompanyCreditRating
          companyName={data.name}
          loading={isRatingRequestPending || isPending}
          ratings={ratings}
          isPublicRating={isPublicRating}
          isRatingSubscription={isRatingSubscription}
          showConflictOfInterests={showConflictOfInterests}
          showRatingRequestInProgress={showRatingRequestInProgress}
          showRequestRatingBox={showRequestRatingBox}
          showRatings={showRatings}
          onRequestClick={handleRequestRatingClick}
        />
      )}
      {isActiveTab(moduleTypes.RATING_NEWS) && (
        <Suspense fallback={<Loader center />}>
          <Paper variant="elevation" elevation={3}>
            <RatingNews />
          </Paper>
        </Suspense>
      )}
      {isActiveTab(moduleTypes.RESEARCH) && (
        <Suspense fallback={<Loader center />}>
          <Paper variant="elevation" elevation={3}>
            <Research />
          </Paper>
        </Suspense>
      )}
      {isActiveTab(moduleTypes.DOCUMENTS) && (
        <Documents
          reports={data.documents.filter(({ type }) => equals(type, 'Rating Report'))}
          performances={data.documents.filter(({ type }) => equals(type, 'Performance Report'))}
          methodologies={data.methodologies}
        />
      )}
      <DisclaimerModal open={showDisclaimerModal} onClose={toggleDisclaimerModal} />
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        onClickCreateNew={createPortfolio.show}
        itemsData={itemsForPortfolio}
        itemType="INSTRUMENT"
      />
      <CreateNewPortfolioModal
        open={createPortfolio.isOpen}
        onClose={createPortfolio.close}
        itemsData={itemsForPortfolio}
        itemType="INSTRUMENT"
      />
    </>
  );
};

InstrumentContent.propTypes = {
  activeTabs: PropTypes.shape({
    companyModule: PropTypes.string,
    companyTab: PropTypes.string,
  }).isRequired,
  setActiveTabs: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  instrumentId: PropTypes.string.isRequired,
  isRatingRequestPending: PropTypes.bool.isRequired,
  isPending: PropTypes.bool.isRequired,
};

export { InstrumentContent };
